import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ButtonStandard, InputWrapper, ModalBase, Text } from "@performyard/py-components";
import { ManageableDocumentEmployee } from "@performyard/service-contracts";

import { SingleSelectEmployee } from "../../../../components/Inputs/SelectEmployee";

type ManageEmployeeDocumentsModalProps = {
	isOpen: boolean;
	onClose: () => void;
	employees: ManageableDocumentEmployee[];
};

const ManageEmployeeDocumentsModal = ({ isOpen, onClose, employees }: ManageEmployeeDocumentsModalProps) => {
	const navigate = useNavigate();
	const [selectedEmployee, setSelectedEmployee] = useState<ManageableDocumentEmployee | null>(null);

	const hasEmployees = employees.length > 0;

	const fetchEmployees = useCallback(
		async (searchTerm: string) =>
			employees.filter((employee) => employee.name.toLowerCase().includes(searchTerm.toLowerCase())),
		[employees]
	);

	const handleClose = () => {
		setSelectedEmployee(null);
		onClose();
	};

	const handleViewDocuments = () => {
		if (!selectedEmployee) {
			return;
		}
		navigate(`/documents/${selectedEmployee.id}`);
		handleClose();
	};

	return (
		<ModalBase isOpen={isOpen} onClose={handleClose}>
			<div className="w-[432px] px-6 pb-6">
				<div className="text-center">
					<Text size="sub-heading" weight="semi-bold">
						{"My Team's Documents"}
					</Text>
					<Text size="paragraph-2" weight="regular">
						View, upload, and manage documents for your team
					</Text>
				</div>
				<div className="pt-4">
					<InputWrapper id="manage-employee-documents-select" label="Employee">
						<SingleSelectEmployee
							id="manage-employee-documents-select"
							fetchEmployees={fetchEmployees}
							value={selectedEmployee}
							onChange={(employeeId, employeeName) =>
								setSelectedEmployee(
									employeeId && employeeName ? { id: employeeId, name: employeeName } : null
								)
							}
							placeholder={hasEmployees ? "Make a selection" : "No eligible employees"}
							disabled={!hasEmployees}
							data-testid="manageEmployeeDocumentsSelect"
						/>
					</InputWrapper>
				</div>
				<div className="flex justify-center gap-2 pt-6">
					<div className="w-fit">
						<ButtonStandard
							variant="outline"
							color="neutral"
							size="large"
							onClick={handleClose}
							data-testid="manageEmployeeDocumentsCancel"
						>
							Cancel
						</ButtonStandard>
					</div>
					<div className="w-fit">
						<ButtonStandard
							color="primary"
							size="large"
							onClick={handleViewDocuments}
							disabled={!selectedEmployee}
							data-testid="manageEmployeeDocumentsView"
						>
							View Documents
						</ButtonStandard>
					</div>
				</div>
			</div>
		</ModalBase>
	);
};

export default ManageEmployeeDocumentsModal;
